import { useState, useEffect } from 'react';
import axios from 'axios';
import { Building2, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function OrgPicker({ onSelect, onSkip }) {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/org/my-organizations`, { withCredentials: true });
        setOrgs(data);
        if (data.length <= 1) {
          onSkip();
        }
      } catch {
        onSkip();
      } finally {
        setLoading(false);
      }
    })();
  }, [onSkip]);

  const handleSelect = async (orgId) => {
    setSwitching(orgId);
    try {
      await axios.post(`${API_URL}/api/org/switch`, { organization_id: orgId }, { withCredentials: true });
      onSelect(orgId);
    } catch {
      onSelect(orgId);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-[#00A6A0]" />
      </div>
    );
  }

  if (orgs.length <= 1) return null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative" data-testid="org-picker">
      <div className="glass-blobs"><div className="blob-3" /></div>
      <div className="noise-overlay" />
      <div className="relative z-10 w-full max-w-md px-4">
        <div className="glass-card rounded-2xl p-8 space-y-6">
          <div className="text-center">
            <h2 className="text-xl font-heading font-bold text-foreground">Select Organization</h2>
            <p className="text-sm text-muted-foreground mt-1">You belong to multiple networks. Choose which one to enter.</p>
          </div>

          <div className="space-y-3">
            {orgs.map(org => (
              <button
                key={org.id}
                onClick={() => handleSelect(org.id)}
                disabled={switching !== null}
                className="w-full flex items-center gap-4 p-4 rounded-xl glass-card glass-card-hover text-left transition-all disabled:opacity-50"
                data-testid={`org-option-${org.id}`}
              >
                {org.logo_url ? (
                  <img src={org.logo_url} alt={org.name} className="w-12 h-12 rounded-xl object-cover border border-border/50" />
                ) : (
                  <div className="w-12 h-12 rounded-xl bg-[#00A6A0]/10 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-[#00A6A0]" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{org.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-muted-foreground font-mono">{org.cisc}</span>
                    {org.is_primary && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#00A6A0]/10 text-[#00A6A0]">Primary</span>
                    )}
                  </div>
                </div>
                {switching === org.id ? (
                  <Loader2 className="w-5 h-5 animate-spin text-[#00A6A0] shrink-0" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrgPicker;
